'use client';

import { useState, useEffect, useCallback } from 'react';
import { cn } from '@/lib/utils';

interface BrandIconProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** External hover control - when provided, disables internal hover handling */
  isHovered?: boolean;
}

const sizeClasses = {
  sm: 'w-5 h-5',
  md: 'w-8 h-8',
  lg: 'w-12 h-12',
  xl: 'w-16 h-16',
};

// Grid cell definitions: [x, y, originalColor]
// Diagonal stripe pattern — a red "wave" sweeping bottom-left to top-right
// Colors: bright red (#ff1f2d) and black (#111111) only
const CELLS: [number, number, string][] = [
  // Row 1
  [0, 0, '#111111'], [24, 0, '#111111'], [48, 0, '#111111'], [72, 0, '#ff1f2d'], [96, 0, '#ff1f2d'],
  // Row 2
  [0, 24, '#111111'], [24, 24, '#111111'], [48, 24, '#ff1f2d'], [72, 24, '#ff1f2d'], [96, 24, '#ff1f2d'],
  // Row 3
  [0, 48, '#111111'], [24, 48, '#ff1f2d'], [48, 48, '#ff1f2d'], [72, 48, '#ff1f2d'], [96, 48, '#111111'],
  // Row 4
  [0, 72, '#ff1f2d'], [24, 72, '#ff1f2d'], [48, 72, '#ff1f2d'], [72, 72, '#111111'], [96, 72, '#111111'],
  // Row 5
  [0, 96, '#ff1f2d'], [24, 96, '#ff1f2d'], [48, 96, '#111111'], [72, 96, '#111111'], [96, 96, '#111111'],
];

const BRIGHT_RED = '#ff1f2d';
const ACCENT_BLUE = '#3B82F6';
const FILL_DURATION = 700; // ms to fill all cells red
const BLUE_TRANSITION = 200; // ms for blue flash
const CELL_COUNT = CELLS.length;

export function BrandIcon({ className, size = 'md', isHovered: externalHovered }: BrandIconProps) {
  const [internalHovered, setInternalHovered] = useState(false);
  const [phase, setPhase] = useState<'idle' | 'filling' | 'blue'>('idle');
  const [filledCount, setFilledCount] = useState(0);

  // Use external hover if provided, otherwise use internal
  const isHovered = externalHovered !== undefined ? externalHovered : internalHovered;
  const useInternalHover = externalHovered === undefined;

  // Handle hover animation
  useEffect(() => {
    if (!isHovered) {
      setPhase('idle');
      setFilledCount(0);
      return;
    }

    // Start filling phase
    setPhase('filling');
    setFilledCount(0);

    const delayPerCell = FILL_DURATION / CELL_COUNT;
    const timeouts: NodeJS.Timeout[] = [];

    // Stagger fill each cell
    for (let i = 0; i < CELL_COUNT; i++) {
      timeouts.push(
        setTimeout(() => {
          setFilledCount(i + 1);
        }, i * delayPerCell)
      );
    }

    // After all cells filled, flash to blue
    timeouts.push(
      setTimeout(() => {
        setPhase('blue');
      }, FILL_DURATION)
    );

    return () => timeouts.forEach(clearTimeout);
  }, [isHovered]);

  // Get fill color for a cell based on current phase and fill progress
  const getCellFill = useCallback((index: number, originalColor: string) => {
    if (phase === 'idle') return originalColor;
    if (phase === 'blue') return ACCENT_BLUE;
    // filling phase: cells up to filledCount are bright red
    return index < filledCount ? BRIGHT_RED : originalColor;
  }, [phase, filledCount]);

  // Transition speed: smooth for all phases
  const getTransition = useCallback(() => {
    if (phase === 'idle') return 'fill 0.3s ease-out';
    if (phase === 'blue') return `fill ${BLUE_TRANSITION}ms ease-out`;
    // Filling: quick snap to red
    return 'fill 0.05s ease-out';
  }, [phase]);

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 116 116"
      role="img"
      aria-label="Takeover grid icon"
      className={cn(
        sizeClasses[size],
        'transition-[filter] duration-300',
        phase === 'blue' && 'drop-shadow-[0_0_8px_rgba(59,130,246,0.5)]',
        className
      )}
      onMouseEnter={useInternalHover ? () => setInternalHovered(true) : undefined}
      onMouseLeave={useInternalHover ? () => setInternalHovered(false) : undefined}
    >
      {CELLS.map(([x, y, originalColor], index) => (
        <rect
          key={index}
          x={x}
          y={y}
          width="20"
          height="20"
          rx="6"
          fill={getCellFill(index, originalColor)}
          style={{ transition: getTransition() }}
        />
      ))}
    </svg>
  );
}
